/**
 * QuizRunner — an untimed practice quiz with instant feedback and an explanation
 * for every answer, ending in a result hero. Shared by Practice and the Review
 * "drill missed" flow. Every answer flows through the progress context (stats +
 * missed set). Ports the design's quiz engine + quizDone. Not the exam: the exam
 * gives no feedback until the end and is graded server-side (see trainingExam).
 */
import { useState } from 'react';
import type { TrainingQuestion } from '@/content/training';
import { useTrainingProgress } from '../TrainingProgressContext';
import { Card, Button } from './TrainingUI';

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

function shuffle<T>(a: T[]): T[] {
  const out = a.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

interface QuizRunnerProps {
  questions: TrainingQuestion[];
  onAgain: () => void;
  onExit: () => void;
}

export function QuizRunner({ questions, onAgain, onExit }: QuizRunnerProps) {
  const progress = useTrainingProgress();
  // Snapshot the deck (question order + per-question choice order) once at mount,
  // via lazy state — the runner is immune to a caller passing a changing
  // `questions` reference mid-run. Callers key the runner to start a fresh run.
  const [items] = useState(() =>
    questions.map((q) => ({ q, order: shuffle(q.choices.map((_, x) => x)) })),
  );

  const [i, setI] = useState(0);
  const [chosen, setChosen] = useState<number | null>(null);
  const [right, setRight] = useState(0);
  const [ended, setEnded] = useState(false);

  const attempted = ended ? i + (chosen !== null ? 1 : 0) : i;

  if (ended || i >= items.length) {
    const total = i >= items.length ? items.length : attempted;
    const pct = total ? Math.round((100 * right) / total) : 0;
    const msg =
      total === 0
        ? 'Session ended.'
        : `That's ${pct}% — ${
            pct >= 90
              ? 'exam-ready territory.'
              : pct >= 80
                ? 'passing, but build more margin.'
                : 'below the 80% pass mark. Hit the lessons and try again.'
          }`;
    return (
      <Card className="result-hero mx-auto max-w-2xl">
        <div className={`big ${pct >= 80 ? 'verdict-pass' : 'verdict-fail'}`}>
          {right} / {total}
        </div>
        <p>{msg}</p>
        <div className="mt-6 flex justify-center gap-3">
          <Button variant="primary" onClick={onAgain}>
            Practice again
          </Button>
          <Button variant="ghost" onClick={onExit}>
            Dashboard
          </Button>
        </div>
      </Card>
    );
  }

  const item = items[i];
  const answered = chosen !== null;

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-3 flex items-center justify-between">
        <div className="q-progress">
          Question {i + 1} of {items.length} · {item.q.topic}
        </div>
        <Button variant="ghost" className="small" onClick={() => setEnded(true)}>
          End session
        </Button>
      </div>
      <div className="exam-bar">
        <div style={{ width: `${(100 * i) / items.length}%` }} />
      </div>

      <Card>
        <div className="q-text">{item.q.q}</div>
        <div>
          {item.order.map((ci, pos) => {
            const isCorrect = ci === item.q.answer;
            const isChosen = ci === chosen;
            let tone = '';
            if (answered) {
              if (isCorrect) tone = ' correct';
              else if (isChosen) tone = ' wrong';
              else tone = ' dim';
            }
            return (
              <button
                key={ci}
                data-testid="quiz-choice"
                disabled={answered}
                onClick={() => {
                  progress.recordAnswer(item.q, ci);
                  setChosen(ci);
                  if (ci === item.q.answer) setRight((r) => r + 1);
                }}
                className={`choice${tone}`}
              >
                <span className="c-letter">{LETTERS[pos]}</span>
                <span>{item.q.choices[ci]}</span>
              </button>
            );
          })}
        </div>
        {answered && (
          <div className={`explain show ${chosen === item.q.answer ? 'good' : 'bad'}`}>
            <div className="verdict">
              {chosen === item.q.answer ? 'Correct' : 'Not quite — the answer is highlighted'}
            </div>
            {item.q.exp}
          </div>
        )}
        {answered && (
          <Button
            variant="primary"
            className="mt-4"
            onClick={() => {
              setChosen(null);
              setI((v) => v + 1);
            }}
          >
            {i < items.length - 1 ? 'Next question →' : 'See result'}
          </Button>
        )}
      </Card>
    </div>
  );
}
